// MACHINERY V2 (Stroje) — the "Obrázky produktu" field (M2.7).
//
// Spec: data package §10 row `productImage (gallery)`. Rules + queue algebra
// live in machineImages.ts; this file is the pixels. Reference impl:
// design_handoff_machinery_v3/machinery-catalog.jsx `ImageDropZone` (lines
// 70-86). Artboards 0802 / 0803 / 0804 — all three draw the EMPTY state, so
// the thumbnail strip below is extrapolated from the established v2 idiom
// (M2.5's docsUploadParts row list) per §9 D4's consistency rule.
//
// THE DROPZONE IS THE SHARED forms/FormDropzone — same cloud glyph, same drag
// tint, same "hint + mono limit" two-line body the designer drew. Its `hint` is
// a plain string, so the designer's teal "klikni na prehľadávanie" fragment
// renders in body colour here; the whole well is the click target either way,
// which is the behaviour that fragment advertises. Splitting the prop into two
// halves to tint four words would change a shipped shared primitive for one
// call site.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import FormDropzone from "../forms/FormDropzone";
import {
  MACHINE_IMAGE_MAX_COUNT,
  MACHINE_IMAGE_MAX_FILE_SIZE_MB,
  MachineImage,
} from "./machineImages";

const StarGlyph: React.FC<{ filled?: boolean }> = ({ filled }) => (
  <svg
    width="11"
    height="11"
    viewBox="0 0 12 12"
    fill={filled ? "currentColor" : "none"}
    stroke="currentColor"
    strokeWidth="1.2"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M6 1.2 L7.5 4.4 L11 4.9 L8.5 7.4 L9.1 10.8 L6 9.2 L2.9 10.8 L3.5 7.4 L1 4.9 L4.5 4.4 Z" />
  </svg>
);

export interface MachineImagesFieldProps {
  images: MachineImage[];
  onFiles: (files: File[]) => void;
  onRemove: (key: string) => void;
  onSetPrimary: (key: string) => void;
  /** Already-translated lines from the last accept pass. */
  rejections: string[];
}

const MachineImagesField: React.FC<MachineImagesFieldProps> = ({
  images,
  onFiles,
  onRemove,
  onSetPrimary,
  rejections,
}) => {
  const { translate } = useTranslation(["myMachinery", "common"]);

  return (
    <div style={{ minWidth: 0 }}>
      <FormDropzone
        hint={translate("myMachinery:v2.add.imagesHint")}
        limit={translate("myMachinery:v2.add.imagesLimit", {
          mb: MACHINE_IMAGE_MAX_FILE_SIZE_MB,
          current: images.length,
          max: MACHINE_IMAGE_MAX_COUNT,
        })}
        onFiles={onFiles}
        invalid={rejections.length > 0}
      />

      {rejections.length > 0 && (
        <div role="alert" style={{ marginTop: 6 }}>
          {rejections.map((line) => (
            <div key={line} style={{ fontSize: 11.5, color: "var(--red)" }}>
              {line}
            </div>
          ))}
        </div>
      )}

      {images.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10 }}>
          {images.map((img) => (
            <div
              key={img.key}
              style={{
                position: "relative",
                width: 74,
                height: 74,
                borderRadius: 8,
                overflow: "hidden",
                background: "var(--bg-soft)",
                border: `1px solid ${img.isPrimary ? "var(--mod-accent)" : "var(--border)"}`,
                flexShrink: 0,
              }}
            >
              <img
                src={img.src}
                alt={img.name}
                style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
              />
              <button
                type="button"
                onClick={() => onSetPrimary(img.key)}
                aria-label={translate("common:gallery.upload.setPrimary")}
                aria-pressed={img.isPrimary}
                style={{
                  position: "absolute",
                  left: 3,
                  bottom: 3,
                  width: 20,
                  height: 20,
                  display: "grid",
                  placeItems: "center",
                  borderRadius: 6,
                  border: 0,
                  cursor: "pointer",
                  background: "rgba(255,255,255,0.88)",
                  color: img.isPrimary ? "var(--mod-accent)" : "var(--muted)",
                }}
              >
                <StarGlyph filled={img.isPrimary} />
              </button>
              <button
                type="button"
                onClick={() => onRemove(img.key)}
                aria-label={translate("common:gallery.upload.remove")}
                style={{
                  position: "absolute",
                  right: 3,
                  top: 3,
                  width: 20,
                  height: 20,
                  display: "grid",
                  placeItems: "center",
                  borderRadius: 6,
                  border: 0,
                  cursor: "pointer",
                  background: "rgba(255,255,255,0.88)",
                  color: "var(--muted)",
                  fontSize: 13,
                  lineHeight: 1,
                }}
              >
                &times;
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MachineImagesField;
